import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import SearchableSelect from "../SearchableSelect";
import useApi from "../../Hooks/useApi";
import * as api from "../../api/getapi";
import * as postapi from "../../api/postapi";
import { toast } from "react-toastify";
import { VoucherConditions } from "./VoucherConditions";
const discountMapping = {
  percentage: { label: "Percentage", value: "Percentage" },
  fixed: { label: "Fixed", value: "Fixed" },
};

const voucherTypeOptions = [
  { label: "Free Shipping", value: "FREE_SHIPPING" },
  // { label: "Shipping Discount", value: "SHIPPING_DISCOUNT" },
  { label: "Price Discount", value: "PRICE_DISCOUNT" },
];
const AddVoucherModal = ({ toggle, close, voucherId }) => {
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState("");
  const [isClicked, setIsClicked] = useState(false);
  const [discountValueValidity, setDiscountValueValidity] = useState(false);
  const [voucher, setVoucher] = useState({
    voucher_code: "",
    voucher_type: "",
    discount_unit: "",
    discount_value: "",
    capped_amount: "",
    min_order_amount: "",
    start_date: "",
    expiry_date: "",
    max_global_usage: "",
    max_per_user_usage: "",
    is_one_time: false,
    is_dummy: false,
    description: "",
  });
  const [voucherConditions, setVoucherConditions] = useState([
    {
      id: 1,
      condition_type: "",
      operator: "",
      value: [],
    },
  ]);

  const minDate = useMemo(() => {
    const today = new Date();
    const nextDay = new Date(today);
    nextDay.setDate(today.getDate() + 1); // Add 1 day to today's date
    return nextDay.toISOString().split("T")[0];
  }, []);
  const minStartDate = useMemo(() => {
    const today = new Date();
    const nextDay = new Date(today);
    nextDay.setDate(today.getDate()); // Add 1 day to today's date
    return nextDay.toISOString().split("T")[0];
  }, []);
  const addVoucher = useApi(postapi.AddVoucher, { hasCatchError: true });
  const fetchVoucherById = useApi(api.fetchVoucherById, { hasCatchError: true });
  const updateVoucher = useApi(postapi.UpdateVoucher, { hasCatchError: true });
  const fetchAllCities = useApi(api.getCitiess);
  const fetchListings = useApi(api.getListingItems);
  const dropdownShops = useApi(api.getDropdownShops);
  const fetchAllUsers = useApi(api.fetchAllMarketPlaceUsers);

  const mapConditionValuesToOptions = (values, options) => {
    if (!Array.isArray(values) || values.length === 0) return [];

    return values.map((val) => {
      const normalizedVal = String(val);
      const matchedOption = options.find((option) => String(option.value) === normalizedVal) || options.find((option) => String(option.label) === normalizedVal);

      return matchedOption || { label: val, value: val };
    });
  };

  // Helper function to get labels for condition values
  const getValueLabels = async (conditionType, values) => {
    if (!Array.isArray(values) || values.length === 0) return [];

    try {
      if (conditionType === "CITY") {
        const res = await fetchAllCities.request();
        const cityOptions =
          res?.data?.map((city) => ({
            label: city.city_name,
            value: city.city_id,
          })) || [];
        return mapConditionValuesToOptions(values, cityOptions);
      } else if (conditionType === "CATEGORY") {
        const categoryOptions = [
          { value: "2", label: "Mobile" },
          { value: "3", label: "Tablet" },
          { value: "4", label: "TV / Monitor" },
          { value: "1", label: "Laptop" },
          { value: "5", label: "Desktop Computer" },
          { value: "6", label: "Accessories" },
        ];
        return mapConditionValuesToOptions(values, categoryOptions);
      } else if (conditionType === "PRODUCT") {
        const res = await fetchListings.request({ filter: "" });
        const productOptions =
          res?.data?.data?.map((product) => ({
            label: product.title,
            value: product.id,
          })) || [];
        return mapConditionValuesToOptions(values, productOptions);
      } else if (conditionType === "SHOP") {
        const res = await dropdownShops.request();
        const shopOptions =
          res?.data?.map((shop) => ({
            label: shop.shop_name,
            value: shop.id,
          })) || [];
        return mapConditionValuesToOptions(values, shopOptions);
      } else if (conditionType === "USER") {
        const res = await fetchAllUsers.request({ orderBy: "createdAt", order: "desc" });
        const userOptions =
          res?.data?.data?.map((user) => ({
            label: `${user.first_name} ${user.last_name}`,
            value: user.uuid,
          })) || [];
        return mapConditionValuesToOptions(values, userOptions);
      } else if (conditionType === "PAYMENT_METHOD") {
        const paymentMethods = [
          // { value: "Bank Transfer", label: "Bank Transfer" },
          { value: "Cash", label: "Cash" },
          { value: "Card", label: "Card" },
          { value: "Card - BNPL", label: "Card - BNPL" },
          { value: "Raast", label: "Raast" },
        ];
        return mapConditionValuesToOptions(values, paymentMethods);
      } else if (conditionType === "MIN_ORDER") {
        return { label: values, value: values };
      } else if (conditionType === "DATE_RANGE") {
        return { label: values, value: values };
      }
      return values.map((val) => ({ label: val, value: val }));
    } catch (error) {
      console.error("Error fetching labels for condition values:", error);
      return values.map((val) => ({ label: val, value: val }));
    }
  };

  useEffect(() => {
    if (voucherId) {
      const fetchVoucher = async () => {
        const res = await fetchVoucherById.request(voucherId);
        if (res.status === 200) {
          const fetched = res.data[0];

          // map backend string values to select options
          const hydratedVoucher = {
            ...fetched,
            voucher_type: voucherTypeOptions.find((opt) => opt.value === fetched.voucher_type) || "",
            discount_unit: discountMapping[fetched.discount_unit?.toLowerCase?.()] || "",
            start_date: fetched.start_date.split("T")[0],
            expiry_date: fetched.expiry_date.split("T")[0],
          };

          // Map conditions with proper labels for values
          const conditions = await Promise.all(
            fetched.conditions?.map(async (condition) => {
              const mappedValues = await getValueLabels(condition.condition_type, condition.value);
              return {
                ...condition,
                condition_type: { label: condition.condition_type.replace(/_/g, " "), value: condition.condition_type },
                operator: { label: condition.operator.replace(/_/g, " "), value: condition.operator },
                value: mappedValues,
              };
            }) || [],
          );

          setVoucher(hydratedVoucher);
          setVoucherConditions(conditions.length ? conditions : [{ id: 1, condition_type: "", operator: "", value: [] }]);
        }
      };
      fetchVoucher();
    }
  }, [voucherId]);
  const handleChange = async (e) => {
    const { name, value } = e.target;
    setVoucher((prev) => ({ ...prev, [name]: value }));
  };
  const handleRequestSubmit = async (e) => {
    try {
      setIsClicked(true);
      e.preventDefault();
      setValidated(true);
      if (isClicked) {
        return;
      }

      // Validate voucher conditions
      const conditionsValid =
        voucherConditions.length > 0 &&
        voucherConditions.every((condition) => {
          return condition.condition_type && condition.operator && condition.value && (Array.isArray(condition.value) ? condition.value.length > 0 : true);
        });

      if (!conditionsValid) {
        setError("All voucher condition fields are required");
        setIsClicked(false);
        return;
      }
      const isFreeShipping = voucher?.voucher_type?.value === "FREE_SHIPPING";

      const valid =
        voucher?.voucher_code &&
        voucher?.voucher_type &&
        voucher.expiry_date &&
        voucher.start_date &&
        voucher.min_order_amount &&
        voucher.description &&
        (isFreeShipping ? true : voucher?.discount_unit?.value && voucher.discount_value) &&
        (voucher?.discount_unit?.value === "percentage" ? voucher.capped_amount : true);

      // const valid =
      //   voucher?.voucher_code && voucher?.voucher_type && voucher?.discount_unit?.value && voucher.discount_value && voucher.expiry_date && voucher?.start_date && (voucher.discount_unit?.value === "percentage" ? voucher.capped_amount : true) && voucher.min_order_amount && voucher.description
      //     ? true
      //     : false;
      if (valid) {
        setValidated(false);
        // Transform voucherConditions to extract only values
        const transformedConditions = voucherConditions.map((condition) => ({
          condition_type: condition.condition_type?.value || condition.condition_type,
          operator: condition.operator?.value || condition.operator,
          value: Array.isArray(condition.value) ? condition.value.map((v) => v?.value || v) : condition.value?.value || condition.value,
        }));

        const data = {
          voucher_code: voucher.voucher_code,
          voucher_type: voucher.voucher_type?.value,
          discount_unit: voucher.discount_unit?.value ?? null,
          discount_value: +voucher.discount_value ?? null,
          capped_amount: +voucher.capped_amount,
          min_order_amount: +voucher.min_order_amount,
          start_date: voucher.start_date.split("T")[0],
          expiry_date: voucher.expiry_date.split("T")[0],
          max_global_usage: voucher.max_global_usage ? +voucher.max_global_usage : null,
          max_per_user_usage: voucher.max_per_user_usage ? +voucher.max_per_user_usage : 1,
          is_one_time: voucher.is_one_time,
          is_dummy: voucher.is_dummy,
          description: voucher.description,
          conditions: transformedConditions,
        };
        if (voucherId) {
          const res = await updateVoucher.request(voucherId, data);
          if (res) {
            toast.success("Voucher Updated Successfully", {
              position: "bottom-right",
              autoClose: 1500,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
            setIsClicked(false);
          }
          toggle((prev) => !prev);
          close();
        } else {
          const res = await addVoucher.request(data);
          if (res) {
            toast.success("Voucher Added Successfully", {
              position: "bottom-right",
              autoClose: 1500,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
            setIsClicked(false);
          }

          toggle((prev) => !prev);
          close();
        }
      }
    } catch (error) {
      setIsClicked(false);
      setError(error.response.data.error.message);
    }
  };
  function generateRandomSixDigitNumber() {
    return Math.floor(100000 + Math.random() * 900000);
  }
  return (
    <>
      <Form noValidate validated={validated} onSubmit={handleRequestSubmit}>
        {error && <div className="text-danger text-center my-1 rounded-1 w-100">*{error}</div>}

        <Row className="">
          <Col md={6} className="py-2">
            <Form.Group className="w-100" controlId="formBasicShopName">
              <label>Voucher Code</label>
              <span className="text-danger">*</span>
              <Form.Control
                value={voucher.voucher_code}
                name="voucher_code"
                onChange={handleChange}
                min={1}
                type="string"
                placeholder="eg.D4UV0001"
                onBlur={(e) => {
                  if (e.target.value !== "") {
                    setVoucher({ ...voucher, voucher_code: e.target.value });
                  }
                }}
                required
              />
              <Form.Control.Feedback type="invalid">Please Enter Voucher Code.</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6} className="py-2">
            <Form.Group className="w-100" controlId="formBasicShopName">
              <label>
                Voucher Type<span className="text-danger">*</span>
              </label>
              <SearchableSelect
                placeholder="Select Voucher Type"
                value={voucher.voucher_type}
                name="voucher_type"
                searchable={true}
                options={voucherTypeOptions}
                handleChange={(e) => {
                  setVoucher((prev) => ({ ...prev, voucher_type: e }));
                }}
                required
              />
              <Form.Control.Feedback type="invalid">* Please Select Voucher Type</Form.Control.Feedback>
            </Form.Group>
          </Col>
          {voucher?.voucher_type?.value === "FREE_SHIPPING" ? (
            <></>
          ) : (
            <>
              <Col md={6} className="py-2">
                <Form.Group className="w-100" controlId="formBasicShopName">
                  <label>
                    Discount Unit<span className="text-danger">*</span>
                  </label>
                  <SearchableSelect
                    placeholder="Select Discount Type"
                    value={voucher.discount_unit}
                    name="discount_unit"
                    searchable={true}
                    options={Object.values(discountMapping)}
                    handleChange={(e) => {
                      setVoucher((prev) => {
                        if (e.value === "Percentage" && +prev?.discount_value > 99) {
                          setDiscountValueValidity(true);
                          return { ...prev, discount_unit: e, discount_value: "" };
                        } else {
                          return { ...prev, discount_unit: e };
                        }
                      });
                    }}
                    required={voucher?.voucher_type?.value === "FREE_SHIPPING" ? false : true}
                  />
                  <Form.Control.Feedback type="invalid">* Please Select Discount Unit</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6} className="py-2">
                <Form.Group className="w-100" controlId="formBasicShopName">
                  <label>Discount Value</label>
                  <span className="text-danger">*</span>
                  <Form.Control
                    value={voucher.discount_value}
                    name="discount_value"
                    onKeyDown={(e) => ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (voucher?.discount_unit?.value === "Percentage") {
                        if (value > 99) {
                          setDiscountValueValidity(true);
                          return;
                        }
                        setDiscountValueValidity(false);
                        handleChange(e);
                      } else {
                        if (value.length > 8) return;
                        handleChange(e);
                      }
                    }}
                    min={1}
                    type="number"
                    placeholder="eg.100"
                    required={voucher?.voucher_type?.value === "FREE_SHIPPING" ? false : true}
                  />
                  <Form.Control.Feedback type="invalid">Please Enter Discount Value.</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </>
          )}

          <Col md={6} className="py-2">
            <Form.Group className="w-100" controlId="formBasicShopName">
              <label>Capped Amount</label>
              {voucher?.discount_unit?.value === "Percentage" ? <span className="text-danger">*</span> : ""}
              <Form.Control
                value={voucher.capped_amount}
                name="capped_amount"
                onKeyDown={(e) => ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()}
                onChange={(e) => {
                  if (e.target.value.length > 8) return;
                  handleChange(e);
                }}
                type="number"
                placeholder="eg.100"
                required={voucher?.discount_unit?.value === "Percentage" ? true : false}
              />
              <Form.Control.Feedback type="invalid">Please Enter Capped Amount.</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6} className="py-2">
            <Form.Group className="w-100" controlId="formBasicShopName">
              <label>Minimum Order Amount</label>
              <span className="text-danger">*</span>
              <Form.Control
                value={voucher.min_order_amount}
                name="min_order_amount"
                onKeyDown={(e) => ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length > 8) return;
                  handleChange(e);
                }}
                type="number"
                placeholder="eg.100"
                required
              />
              <Form.Control.Feedback type="invalid">Please Enter Minimum Order Amount.</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6} className="py-2">
            <Form.Group className="w-100" controlId="formBasicShopName">
              <label>Start Date</label>
              <span className="text-danger">*</span>
              <Form.Control value={voucher.start_date} min={minStartDate} name="start_date" onChange={handleChange} type="date" required />
              <Form.Control.Feedback type="invalid">Please Enter Voucher Start Date.</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6} className="py-2">
            <Form.Group className="w-100" controlId="formBasicShopName">
              <label>Expiry Date</label>
              <span className="text-danger">*</span>
              <Form.Control value={voucher.expiry_date} min={minDate} name="expiry_date" onChange={handleChange} type="date" required />
              <Form.Control.Feedback type="invalid">Please Enter Voucher Expiry Date.</Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6} className="py-2">
            <Form.Group className="w-100" controlId="formBasicShopName">
              <label>Max Global Usage</label>
              <Form.Control
                value={voucher.max_global_usage}
                name="max_global_usage"
                onKeyDown={(e) => ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length > 8) return;
                  handleChange(e);
                }}
                type="number"
                placeholder="eg.100"
              />
              <Form.Control.Feedback type="invalid">Please Enter Maximum Global Usage.</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6} className="py-2">
            <Form.Group className="w-100" controlId="formBasicShopName">
              <label>Max Per User Usage</label>
              <Form.Control
                value={voucher.max_per_user_usage}
                name="max_per_user_usage"
                onKeyDown={(e) => ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length > 8) return;
                  handleChange(e);
                }}
                type="number"
                placeholder="eg.100"
              />
              <Form.Control.Feedback type="invalid">Please Enter Maximum Per User Usage.</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6} className="py-2">
            <Form.Group>
              <Form.Check type="checkbox" label="Is One Time" checked={voucher.is_one_time} onChange={(e) => setVoucher({ ...voucher, is_one_time: e.target.checked })} />
            </Form.Group>
          </Col>
          <Col md={6} className="py-2">
            <Form.Group>
              <Form.Check type="checkbox" label="Is Dummy" checked={voucher.is_dummy} onChange={(e) => setVoucher({ ...voucher, is_dummy: e.target.checked })} />
            </Form.Group>
          </Col>
          <hr />
          <VoucherConditions voucherConditions={voucherConditions} setVoucherConditions={setVoucherConditions} />
          <Col md={12} className="py-2">
            <Form.Group>
              <label>Description</label>
              <span className="text-danger">*</span>
              <Form.Control name="description" required value={voucher.description} as="textarea" rows={3} onChange={handleChange} />
              <Form.Control.Feedback type="invalid">Enter Description.</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        {discountValueValidity && (
          <div class="alert alert-warning  text-center w-100 mt-2" role="alert">
            {"Discount Value must be less than 100%"}
          </div>
        )}
        <Row className="py-3">
          <Col>
            <Button
              className="w-100"
              onClick={handleRequestSubmit}
              disabled={
                isClicked ||
                !(
                  voucher?.voucher_code &&
                  voucher?.voucher_type &&
                  voucher.expiry_date &&
                  voucher.start_date &&
                  voucher.min_order_amount &&
                  voucher.description &&
                  (voucher?.voucher_type?.value === "FREE_SHIPPING" ? true : voucher?.discount_unit?.value && voucher.discount_value) &&
                  (voucher?.discount_unit?.value === "percentage" ? voucher.capped_amount : true)
                )
                  ? true
                  : false
              }
            >
              Save
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default AddVoucherModal;
